import { EventPreviewModel } from "client/Api/Hub/Events/eventViewer";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { EMPTY_ARRAY, isNotNil } from "client/Misc/utils";
import { ReactElement, useMemo } from "react";
import { getEventLocationLabel } from "../Card/EventItemCard";
import { HubItemStatistic, ListItemProps } from "../common";
import { ListItemBase } from "./ListItemBase";
import { NumberFormatter } from "client/Hooks";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { ReactionStatistic, useReactionStatistic } from "client/Features/Reactions/useReactionStatistic";

export type EventListItemProps = ListItemProps<EventPreviewModel>;

export const EventListItem = (props: EventListItemProps): ReactElement => {
	const {
		item,
		isWideTile,
		isUltraWideTile,
		onOpen,
		timezone
	} = props;

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();
	const reactionStatistic: ReactionStatistic = useReactionStatistic(item.reactionCounters);

	const statistics = useMemo(
		(): readonly HubItemStatistic[] => {
			const statistics: HubItemStatistic[] = [];

			if (isNotNil(item.attendeeCount)) {
				statistics.push(
					{
						icon: "user-group",
						text: statisticsFormatter(item.attendeeCount),
						trackingId: "attendee-count"
					}
				);
			}

			const locationLabel: string | null = getEventLocationLabel(item);
			if (isNotNil(locationLabel)) {
				statistics.push(
					{
						icon: "location-dot",
						text: locationLabel,
						trackingId: "location"
					}
				);
			}

			statistics.push({
				icon: reactionStatistic.icon,
				text: statisticsFormatter(reactionStatistic.total),
				trackingId: "reaction-count"
			});

			if (item.areCommentsEnabled) {
				statistics.push({
					icon: "comment",
					text: statisticsFormatter(item.commentsCount),
					trackingId: "comment-count"
				});
			}

			return statistics;
		},
		[
			item,
			reactionStatistic.icon,
			reactionStatistic.total,
			statisticsFormatter
		]
	);

	const categoryNames = useMemo(
		(): readonly string[] => {
			return isNotNil(item.category)
				? [
					item.category
						.name
				]
				: EMPTY_ARRAY;
		},
		[item.category]
	);

	return (
		<ListItemBase
			itemKind={HubItemKind.Event}
			id={item.id}
			title={item.title}
			media={item.media}
			startDate={item.startDate}
			statistics={statistics}
			categoryNames={categoryNames}
			descriptionPreview={item.descriptionPreview}
			isWideTile={isWideTile}
			trackingIdBase="event-item"
			onOpen={onOpen}
			authorName={item.author?.name ?? ""}
			isUltraWideTile={isUltraWideTile}
			timezone={timezone}
			isNew={item.isNew}
		/>
	);
};
EventListItem.displayName = "EventListItem";
